import React, { useRef, useState } from "react"
import Modal from "aldoo-ra/Modal"
import CMSRender from "aldoo-ra/CMS/cms-render"
import Button from "aldoo-ra/Button"
import Typography from "aldoo-ra/Typography"
import Localization from "aldoo-ra/Localization"
import { twMerge } from "tailwind-merge"

export default function SigninDialog({ controller }) {
  const emailRef = useRef()
  const { tjsx } = Localization()
  const { closeDialog, signin, DialogType, isDialogOpen } = controller

  // State to track if the email input is filled
  const [isEmailEntered, setIsEmailEntered] = useState(false)

  return (
    <Modal
      isOpen={isDialogOpen(DialogType.SIGNIN)}
      onClose={closeDialog}
      variant="mobilingo_big"
      className="max-w-[534px]"
    >
      <CMSRender
        contentType="SigninDialog"
        renderItem={(dialog) => {
          const emailProvider = dialog.providers.find((p) => p.id === "email")

          return (
            <div className="relative w-full flex flex-col items-center justify-center bg-theme-white md:px-[56px] pb-[12px] mx-auto">
              <div className="w-full max-w-[374px]">
                {/* Logo */}
                <div className="flex items-center justify-center mb-12">
                  <img src={dialog.logo} alt="Mobilingo" className="h-[53px]" />
                </div>
                {/* Providers */}
                <div className="space-y-3">
                  {dialog.providers
                    .filter((item) => item.id !== "email")
                    .map((provider) => {
                      let providerClassName
                      switch (provider.id) {
                        case "apple":
                          providerClassName = "bg-gray_900"
                          break
                        case "google":
                          providerClassName = "bg-[#4285F4]"
                          break
                        default:
                          providerClassName = "bg-gray_100 text-text"
                      }

                      return (
                        <Button
                          key={provider.id}
                          onClick={() => signin(provider)}
                          variant="sign_in_option"
                          className={twMerge(providerClassName)}
                        >
                          <img
                            src={provider.logo}
                            alt={provider.name}
                            className="w-6 h-6"
                          />
                          <span>{provider.cta}</span>
                        </Button>
                      )
                    })}
                </div>

                {/* Divider ("OR") */}
                <div className="text-center mt-[15px] mb-[38px]">
                  <Typography variant="body1_dim">
                    {dialog.divider_text}
                  </Typography>
                </div>

                {/* Email Input */}
                <div className="mb-4">
                  <div className="flex items-center gap-[10px] px-[20px] py-[10px] bg-gray_100 border border-bordered rounded-full">
                    <img
                      src={emailProvider?.logo}
                      alt="Email Provider"
                      className="w-6 h-6 opacity-50"
                    />
                    <Typography variant="body1" className="my-0 flex-1 min-w-0">
                      <input
                        type="email"
                        ref={emailRef}
                        placeholder={emailProvider?.placeholder}
                        onChange={(e) =>
                          setIsEmailEntered(!!e.target.value.trim())
                        }
                        className="w-full text-text placeholder-gray_700 bg-transparent outline-none"
                      />
                    </Typography>
                  </div>
                </div>

                {/* Sign In Button */}
                <Button
                  onClick={() => {
                    const email = emailRef.current?.value.trim().toLowerCase()
                    if (email) signin(emailProvider, email)
                  }}
                  variant={
                    isEmailEntered
                      ? "sign_in_option"
                      : "sign_in_option_disabled"
                  }
                  className={twMerge(
                    isEmailEntered
                      ? "bg-gradient-to-t from-grad_blue_dk to-grad_blue_lt hover:bg-gradient-to-t hover:from-primary hover:to-secondary"
                      : "bg-gray_300 cursor-not-allowed"
                  )}
                  disabled={!isEmailEntered} // Prevent action if disabled
                >
                  {emailProvider?.cta}
                </Button>

                {/* Footer */}
                <div className="mt-[40px] text-center">
                  <Typography variant="txt1_dim">
                    {tjsx(dialog.footer_text, {
                      tos: dialog.footer_links?.[0]?.link?.[0]?.url ? (
                        <a
                          key={dialog.footer_links[0]._id}
                          href={dialog.footer_links[0].link[0].url}
                          className="text-primary hover:underline"
                        >
                          {dialog.footer_links[0].label}
                        </a>
                      ) : null,
                      pp: dialog.footer_links?.[1]?.link?.[0]?.url ? (
                        <a
                          key={dialog.footer_links[1]._id}
                          href={dialog.footer_links[1].link[0].url}
                          className="text-primary hover:underline"
                        >
                          {dialog.footer_links[1].label}
                        </a>
                      ) : null,
                    })}
                  </Typography>
                </div>
              </div>
            </div>
          )
        }}
      />
    </Modal>
  )
}
